'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import { MdExitToApp, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getDischarge,
    createDischarge,
    updateDischarge,
    CreateDischargeData,
    DischargeType,
    DISCHARGE_TYPE_LABELS,
} from '@/lib/api/hospital'
import { getActiveAdmissions, Admission } from '@/lib/api/hospital'

export default function DischargeFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)
    const company = useSelector((state: RootState) => state.auth.company)
    const companyId = company?.id || 0

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [formData, setFormData] = useState<CreateDischargeData>({
        admissionId: 0,
        clientId: 0,
        dischargeType: 'DISCHARGE_HOME',
        dischargeDiagnosis: '',
        cid10Code: '',
        clinicalSummary: '',
        orders: '',
        medications: '',
        followUpInstructions: '',
        dietInstructions: '',
        activityRestrictions: '',
        warningSignals: '',
        returnAppointment: '',
        referrals: '',
        notes: '',
    })

    useEffect(() => {
        loadAdmissions()
        if (isEditing && id) {
            loadDischarge(parseInt(id))
        }
    }, [id, isEditing])

    const loadAdmissions = async () => {
        if (!companyId) return
        try {
            const response = await getActiveAdmissions(companyId)
            if (response.success) {
                setAdmissions(response.result)
            }
        } catch (error) {
            console.error('Error loading admissions:', error)
        }
    }

    const loadDischarge = async (dischargeId: number) => {
        setLoading(true)
        try {
            const response = await getDischarge(dischargeId)
            if (response.success) {
                const disch = response.result
                setFormData({
                    admissionId: disch.admissionId,
                    clientId: disch.clientId,
                    dischargeType: disch.dischargeType,
                    dischargeDiagnosis: disch.dischargeDiagnosis || '',
                    cid10Code: disch.cid10Code || '',
                    clinicalSummary: disch.clinicalSummary || '',
                    orders: disch.orders || '',
                    medications: disch.medications || '',
                    followUpInstructions: disch.followUpInstructions || '',
                    dietInstructions: disch.dietInstructions || '',
                    activityRestrictions: disch.activityRestrictions || '',
                    warningSignals: disch.warningSignals || '',
                    returnAppointment: disch.returnAppointment?.split('T')[0] || '',
                    referrals: disch.referrals || '',
                    notes: disch.notes || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar alta')
                navigate('/hospital/discharges')
            }
        } catch (error) {
            toast.error('Erro ao carregar alta')
            navigate('/hospital/discharges')
        } finally {
            setLoading(false)
        }
    }

    const handleAdmissionChange = (admissionId: number) => {
        const admission = admissions.find(a => a.id === admissionId)
        setFormData({
            ...formData,
            admissionId,
            clientId: admission?.clientId || 0,
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.admissionId) {
            toast.error('Selecione uma internação')
            return
        }

        setSaving(true)
        try {
            const data = {
                ...formData,
                dischargeDiagnosis: formData.dischargeDiagnosis || undefined,
                cid10Code: formData.cid10Code || undefined,
                clinicalSummary: formData.clinicalSummary || undefined,
                orders: formData.orders || undefined,
                medications: formData.medications || undefined,
                followUpInstructions: formData.followUpInstructions || undefined,
                dietInstructions: formData.dietInstructions || undefined,
                activityRestrictions: formData.activityRestrictions || undefined,
                warningSignals: formData.warningSignals || undefined,
                returnAppointment: formData.returnAppointment || undefined,
                referrals: formData.referrals || undefined,
                notes: formData.notes || undefined,
            }

            let response
            if (isEditing && id) {
                response = await updateDischarge(parseInt(id), data)
            } else {
                response = await createDischarge(data)
            }

            if (response.success) {
                toast.success(
                    isEditing ? 'Alta atualizada com sucesso' : 'Alta solicitada com sucesso'
                )
                navigate('/hospital/discharges')
            } else {
                toast.error(response.error || 'Erro ao salvar alta')
            }
        } catch (error) {
            toast.error('Erro ao salvar alta')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/discharges')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdExitToApp className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Alta' : 'Solicitar Alta Hospitalar'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da alta'
                                : 'Solicite a alta do paciente'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Informações Gerais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Internação *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.admissionId}
                                onChange={e => handleAdmissionChange(parseInt(e.target.value))}
                                required
                            >
                                <option value={0}>Selecione uma internação</option>
                                {admissions.map(adm => (
                                    <option key={adm.id} value={adm.id}>
                                        {adm.client?.name} - Leito {adm.bed?.number || 'S/N'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo de Alta *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.dischargeType}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        dischargeType: e.target.value as DischargeType,
                                    })
                                }
                                required
                            >
                                {Object.entries(DISCHARGE_TYPE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Diagnóstico de Alta</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Diagnóstico principal"
                                value={formData.dischargeDiagnosis}
                                onChange={e =>
                                    setFormData({ ...formData, dischargeDiagnosis: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">CID-10</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: J18.9"
                                value={formData.cid10Code}
                                onChange={e =>
                                    setFormData({ ...formData, cid10Code: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data de Retorno</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.returnAppointment}
                                onChange={e =>
                                    setFormData({ ...formData, returnAppointment: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Resumo Clínico</h3>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Resumo da Internação</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-24 w-full"
                                placeholder="Resumo da internação..."
                                value={formData.clinicalSummary}
                                onChange={e =>
                                    setFormData({ ...formData, clinicalSummary: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Procedimentos Realizados</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Procedimentos..."
                                value={formData.orders}
                                onChange={e =>
                                    setFormData({ ...formData, orders: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Orientações para Alta</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Medicamentos</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Prescrição domiciliar..."
                                value={formData.medications}
                                onChange={e =>
                                    setFormData({ ...formData, medications: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Orientações de Seguimento</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Retornos, exames..."
                                value={formData.followUpInstructions}
                                onChange={e =>
                                    setFormData({ ...formData, followUpInstructions: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Dieta</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Orientações dietéticas..."
                                value={formData.dietInstructions}
                                onChange={e =>
                                    setFormData({ ...formData, dietInstructions: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Restrições de Atividade</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Restrições..."
                                value={formData.activityRestrictions}
                                onChange={e =>
                                    setFormData({ ...formData, activityRestrictions: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Sinais de Alerta</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Quando procurar atendimento..."
                                value={formData.warningSignals}
                                onChange={e =>
                                    setFormData({ ...formData, warningSignals: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Encaminhamentos</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Especialidades..."
                                value={formData.referrals}
                                onChange={e =>
                                    setFormData({ ...formData, referrals: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Observações</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered h-24 w-full"
                            placeholder="Observações adicionais..."
                            value={formData.notes}
                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/discharges')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Solicitar Alta'}
                    </button>
                </div>
            </form>
        </div>
    )
}
